import React from 'react';
import PropTypes from 'prop-types';

import withTheme from 'core/hocs/withTheme';
import themePropTypes from 'core/utils/prop-types/theme';
import { sizeTypes } from 'core/utils/prop-types/common';

import { resolveScopedStyles } from 'core/utils/styled-jsx';

import Input from 'core/components/Input';
import Button from 'core/components/Button';

import styles from './index.styl';

const sizeContract = `
  {
    height,
    padding,
  }
`;

const iconStylesContract = '{ color }';

const iconStatesContract = `
  {
    idle: ${iconStylesContract},
    hover: ${iconStylesContract},,
    focused: ${iconStylesContract},,
  }
`;


/**
 * @author Pavel Povalyaev (<p.povalyaev@rambler-co.ru>)
 */

/* eslint-disable react/prop-types */
class SearchInput extends React.PureComponent {
  static propTypes = {
    /**
     * Функция, получающая в качестве аргумента ref инпута
     */
    innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

    /**
     * Показывать ли полноценную кнопку рядом с инпутом вместо иконки.
     * Это свойство переопределяет значение из темы
     */
    withButton: PropTypes.bool,

    /**
     * Скрывает иконку поиска внутри инпута
     */
    withoutIcon: PropTypes.bool,

    /**
     * Размер поля
     */
    size: sizeTypes,

    /**
     * Размер иконки в инпуте.
     * Иконка должна быть квадратом.
     * Свойство переопределяет width и height.
     */
    iconSize: PropTypes.number,


    /** @ignore */
    theme: themePropTypes(`
      {
        animations: {
          hover
        },
        controls: {
          input: {
            basic: {
              idle: {
                background,
              }
            }
            const: {
              radius,
              sizes: {
                small: ${sizeContract},
                medium: ${sizeContract},
                large: ${sizeContract},
              },
            },
          },
          searchInput: {
            placeholder,
            withButton,
            icons: {
              Search,
              Close,
            },
            iconStyles: {
              withoutText: ${iconStatesContract},
              withText: ${iconStatesContract}
            }
          }
        },
        colors: {
          active1,
          primary,
        }
      }
    `),
  };

  static defaultProps = {
    size: 'medium',
  };

  static getDerivedStateFromProps(props, state) {
    if (props.defaultValue !== state.defaultValue) {
      return {
        value: props.defaultValue || '',
        defaultValue: props.defaultValue,
      };
    }

    return null;
  }

  state = {
    value: this.props.defaultValue || '',
  };

  onChange = e => {
    this.setState({
      value: e.target.value,
    });

    if (typeof this.props.onChange === 'function') {
      this.props.onChange(e);
    }
  };

  render() {
    const {
      size,
      iconSize: iconSizeFromProps,
      theme: {
        controls: {
          input: inputAtoms,
          searchInput: searchInputAtoms,
        },
      },
      withButton: withButtonProp,
      withoutIcon,
      defaultValue,
      ...inputProps
    } = this.props;

    const {
      placeholder,
      withButton: withButtonAtoms,
      iconStyles: iconAtoms,
      icons: {
        Search,
        Close,
      },
    } = searchInputAtoms;

    const hasText = !!this.state.value;

    const withButton = withButtonProp !== undefined
      ? withButtonProp
      : withButtonAtoms;

    const iconAtomsByState = iconAtoms[hasText ? 'withText' : 'withoutText'];

    const scoped = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .input
            ~ .icons
              :global(.${styles.icon})
                color ${iconAtomsByState.idle.color}

            &:hover ~ .icons
              :global(.${styles.icon})
                color ${iconAtomsByState.hover.color}

            &:focus ~ .icons
              :global(.${styles.icon})
                color ${iconAtomsByState.focused.color}

            &:disabled ~ .icons
              :global(.${styles.icon})
                color ${iconAtomsByState.disabled.color}
        `}</style>
      </scope>
    );

    let iconSize = iconSizeFromProps;

    if (!iconSize) {
      /**
       * Вычисляем высоту иконки, потому что некоторые браузеры (Safari)
       * не могут подогнать ее высоту под размер контейнера.
       */
      const inputSizeAtoms = inputAtoms.const.sizes[size];
      const inputHeight = parseInt(inputSizeAtoms.height, 10);
      const inputPaddingTop = parseInt(inputSizeAtoms.padding, 10);
      iconSize = inputHeight - (inputPaddingTop + 2) * 2;
    }

    const clearable = this.state.value && this.state.value === defaultValue;

    const iconProps = {
      className: scoped.wrapClassNames(styles.icon),
      preserveAspectRatio: 'xMaxYMid',
      width: iconSize,
      height: iconSize,
    };

    return (
      <div className={styles.wrapper} data-qa='search-input'>
        <Input
          placeholder={placeholder}
          autoComplete='off'
          {...inputProps}
          size={size}
          onBlur={this.props.onBlur}
          onFocus={this.props.onFocus}
          onChange={this.onChange}
          className={styles.label}
          inputClassName={scoped.wrapClassNames('input')}
          iconClassName={scoped.wrapClassNames('icons')}
          value={this.state.value}
        >
          {clearable && (
            <button
              className={scoped.wrapClassNames(styles.submit)}
              type='reset'
            >
              <Close {...iconProps} />
            </button>
          )}
          {!clearable && !withButton && !withoutIcon && (
            <button
              className={scoped.wrapClassNames(styles.submit)}
              type='submit'
              aria-label='Найти'
              data-qa='search-submit-icon'
            >
              <Search {...iconProps} />
            </button>
          )}
        </Input>
        {withButton && (
          <Button
            className={styles.button}
            typeAttribute='submit'
            size={size}
            minWidth={0}
          >
            Найти
          </Button>
        )}
        <scoped.styles />
      </div>
    );
  }
}

const SearchInputWithHOCs = withTheme(SearchInput);
SearchInputWithHOCs.displayName = 'SearchInput';

export { SearchInput as StorybookComponent };
export default SearchInputWithHOCs;
